import type { MegaMenuProps } from "./types";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@navikt/aksel-icons";
import { Button as DsButton, Link } from "@digdir/designsystemet-react";

export default function MegaMenu(props: MegaMenuProps) {
  const {
    menuItems,
    districts,
    activities,
    isFrontPage,
    parentPage,
    currentPage,
  } = props.megaMenuData;

  const menuRef = useRef<HTMLDivElement>(null);
  const grenerRef = useRef<HTMLDivElement>(null);
  const kretserRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<"main" | "grener" | "kretser">("main");
  const isActive = useCallback((v: typeof view) => view === v, [view]);

  // Track screensize to set mega menu height
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 992 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setIsSmallScreen(window.innerWidth < 992);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Close menu on escape or outside click
  useEffect(() => {
    if (!props.visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        props.onClose?.();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        props.onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.visible, props.onClose]);

  // Set menu height based on header and content
  const [menuHeight, setMenuHeight] = useState({ height: `600px` });
  useLayoutEffect(() => {
    if (isSmallScreen && props.headerHeight) {
      setMenuHeight({ height: `calc(100vh - ${props.headerHeight}px)` });
      return;
    }
    const grenerHeight = grenerRef.current?.offsetHeight;
    const kretserHeight = kretserRef.current?.offsetHeight;
    if (view === "grener") {
      setMenuHeight({ height: `${grenerHeight}px` });
    } else if (view === "kretser") {
      setMenuHeight({ height: `${kretserHeight}px` });
    } else {
      setMenuHeight({ height: `fit-content` });
    }
  }, [view, isSmallScreen, props.headerHeight, isActive]);

  return (
    <nav
      ref={menuRef}
      aria-label="Hovedmeny"
      className={`absolute top-full z-20 w-full px-4 bg-tinted-neutral mt-[1px] shadow-lg transition duration-150 ease-out
        ${
          props.visible
            ? "opacity-100 translate-y-0 h-fit"
            : "opacity-0 -translate-y-2 pointer-events-none h-0"
        }
      `}
    >
      <div
        style={menuHeight}
        className={`relative h-auto max-h-screen max-w-content pb-4 mx-auto overflow-y-auto overflow-x-hidden min-h-56
          ${
            props.visible
              ? "opacity-100 py-11 lg:py-13"
              : "opacity-0 pointer-events-none h-0"
          }
        `}
      >
        {/* Main menu */}
        {view === "main" && (
          <div
            className={`transition-transform duration-200 ${
              view === "main" ? "translate-x-0" : "-translate-x-full"
            }
            `}
          >
            <div className="flex flex-col mx-auto w-full gap-10 sm:px-6 lg:flex-row">
              <ul className="order-2 flex flex-col gap-4 w-full px-2 lg:order-first">
                {!isFrontPage && parentPage && (
                  <Link
                    href={parentPage.url}
                    className="flex items-center gap-1 w-fit -ml-1 text-sm text-neutralNsf-subtle no-underline decoration-2 hover:underline focus-visible:text-white sm:-ml-7"
                  >
                    <ArrowLeftIcon aria-hidden fontSize="1.5rem" />
                    {parentPage.title}
                  </Link>
                )}
                {!isFrontPage && currentPage && (
                  <Link
                    href={currentPage.url}
                    className="text-neutralNsf text-xl no-underline decoration-2 hover:underline focus-visible:text-white"
                  >
                    <h2 className="heading-sm mb-0">{currentPage.title}</h2>
                  </Link>
                )}

                {menuItems.map((item) => (
                  <li key={item.id ?? item.url}>
                    <Link
                      href={item.url}
                      tabIndex={props.visible && view === "main" ? 0 : -1}
                      className="w-full text-lg text-neutralNsf no-underline decoration-2 outline-[3px] hover:underline focus-visible:outline-focusOuter focus-visible:outline focus-visible:text-white"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
              {isFrontPage && (
                <ul className="flex flex-col gap-5 w-full order-first lg:order-last">
                  {/* "Grener"-button */}
                  <li>
                    <button
                      type="button"
                      onClick={() => setView("grener")}
                      tabIndex={props.visible && view === "main" ? 0 : -1}
                      className="flex items-center justify-between w-full h-full p-4.5 heading-xs font-body font-medium border outline-[3px] focus-visible:outline-focusOuter focus-visible:outline md:p-5.5 bg-white hover:bg-neutralNsf-surface-hover active:bg-neutralNsf-surface-active text-neutralNsf border-neutralNsf-subtle"
                    >
                      <span>Grener</span>
                      <ArrowRightIcon aria-hidden fontSize="1.5rem" />
                    </button>
                  </li>
                  {/* "Kretser"-button */}
                  <li>
                    <button
                      type="button"
                      onClick={() => setView("kretser")}
                      tabIndex={props.visible && view === "main" ? 0 : -1}
                      className="flex items-center justify-between w-full h-full p-4.5 heading-xs font-body font-medium border outline-[3px] focus-visible:outline-focusOuter focus-visible:outline md:p-5.5 bg-white hover:bg-neutralNsf-surface-hover active:bg-neutralNsf-surface-active text-neutralNsf border-neutralNsf-subtle"
                    >
                      <span>Kretser</span>
                      <ArrowRightIcon aria-hidden fontSize="1.5rem" />
                    </button>
                  </li>
                </ul>
              )}

              {/* "Bli klubbmedlem"-button */}
              {props.memberPageUrl && (
                <DsButton
                  asChild
                  variant="primary"
                  className="order-last flex min-h-10.5 h-10.5 w-fit border lg:hidden"
                >
                  <a href={props.memberPageUrl} className="text-lg">
                    Bli klubbmedlem
                  </a>
                </DsButton>
              )}
            </div>
          </div>
        )}
        {/* Grener */}
        <div
          ref={grenerRef}
          className={`absolute inset-0 py-10 transition-transform duration-150 ${
            view === "grener"
              ? "translate-x-0 overflow-y-auto h-fit"
              : "translate-x-full overflow-y-hidden h-auto"
          }`}
        >
          {view === "grener" && (
            <div
              className="flex flex-col gap-4 mx-auto w-full sm:px-6"
              aria-label="Grener"
            >
              <button
                onClick={() => setView("main")}
                className="flex gap-1 items-center w-fit -ml-1 mb-2 pl-0 bg-transparent text-sm font-body text-neutralNsf no-underline decoration-2 hover:underline sm:-ml-7"
              >
                <ArrowLeftIcon aria-hidden fontSize="1.5rem" />
                Hovedmeny
              </button>
              <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {activities.map((a) => (
                  <li key={a.id ?? a.url}>
                    <Link
                      href={a.url}
                      className="flex items-center justify-between w-full h-full p-4.5 heading-xs font-medium no-underline border outline-[3px] focus-visible:outline-focusOuter focus-visible:outline md:p-5.5 bg-white hover:bg-neutralNsf-surface-hover active:bg-neutralNsf-surface-active text-neutralNsf border-neutralNsf-subtle"
                    >
                      {a.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Kretser */}
        <div
          ref={kretserRef}
          className={`absolute inset-0 py-10 transition-transform duration-150 ${
            view === "kretser"
              ? "translate-x-0 overflow-y-auto h-fit"
              : "translate-x-full overflow-y-hidden h-auto"
          }`}
        >
          {view === "kretser" && (
            <div
              className="flex flex-col gap-4 mx-auto w-full sm:px-6"
              aria-label="Kretser"
            >
              <button
                onClick={() => setView("main")}
                className="flex gap-1 items-center w-fit -ml-1 mb-2 pl-0 bg-transparent text-sm font-body text-neutralNsf no-underline decoration-2 hover:underline sm:-ml-7"
              >
                <ArrowLeftIcon aria-hidden fontSize="1.5rem" />
                Hovedmeny
              </button>
              <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {districts.map((d) => (
                  <li key={d.id ?? d.url}>
                    <Link
                      href={d.url}
                      className="flex items-center justify-between w-full h-full p-4.5 heading-xs font-medium no-underline border outline-[3px] focus-visible:outline-focusOuter focus-visible:outline md:p-5.5 bg-white hover:bg-neutralNsf-surface-hover active:bg-neutralNsf-surface-active text-neutralNsf border-neutralNsf-subtle"
                    >
                      {d.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
